import { X } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Skeleton, cn } from "@ncp/ui";
import { usePlatformServices } from "./api";

/**
 * Chooses the services an API client or webhook is limited to: the compiled procedures and the playbooks, from the published catalogue.
 * Keys that are no longer published stay selected (and removable) so saving never silently widens a restriction.
 */
export function ServicePicker({ source, value, onChange }: { source: "api-clients" | "webhooks"; value: string[]; onChange: (next: string[]) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const services = usePlatformServices(source);
  if (services.isLoading) return <Skeleton className="h-16 rounded-lg" />;
  const all = services.data ?? [];
  const known = new Set(all.map((s) => s.key));
  const toggle = (key: string) => onChange(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]);
  const groups = [
    { key: "compiled", title: t("admin:identity.apiClients.procedures"), items: all.filter((s) => s.kind === "compiled") },
    { key: "playbook", title: t("admin:identity.apiClients.playbooks"), items: all.filter((s) => s.kind === "playbook") },
  ].filter((g) => g.items.length > 0);
  const orphans = value.filter((k) => !known.has(k));
  return (
    <div className="space-y-2.5">
      {groups.map((g) => (
        <div key={g.key}>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-sand-600">{g.title}</p>
          <div className="flex flex-wrap gap-1.5">
            {g.items.map((s) => {
              const on = value.includes(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  title={s.key}
                  aria-pressed={on}
                  onClick={() => toggle(s.key)}
                  className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors", on ? "border-aegold-400 bg-aegold-50 text-aegold-900" : "border-sand-300 hover:border-aegold-300")}
                >
                  <span className="font-mono text-[10px] uppercase text-sand-600">{s.code}</span>
                  <span>{locale === "ar" && s.nameAr ? s.nameAr : s.name}</span>
                  {s.kind === "playbook" && s.acceptsExternal ? <Badge size="sm" variant="info">{t("admin:identity.apiClients.eServices")}</Badge> : null}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {orphans.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-warning-700">{t("admin:identity.apiClients.unpublished")}</span>
          {orphans.map((k) => (
            <button key={k} type="button" onClick={() => toggle(k)} className="inline-flex items-center gap-1 rounded-full border border-warning-300 bg-warning-50 px-2 py-0.5 font-mono text-[11px] text-warning-800" aria-label={t("admin:identity.apiClients.removeService", { key: k })}>
              {k} <X className="size-3" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
